/**
 * Module responsible to hold some utility functions used by Rudderstack client
 * - ie: Transformations, validations, User Traits logic, etc
 *
 */
import 'vite/modulepreload-polyfill';

import {
  COLLECTION_PARENT_TYPES,
  COLLECTION_TYPES,
  PRICE_TIER,
  QUERYSTRING_API,
} from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import * as CustomerUtils from '~/utils/customer';
import * as CypherUtils from '~/utils/cypher';
import * as HashUtils from '~/utils/hash';
import * as LoopReturnsOnstoreUtils from '~/utils/loop-returns-onestore';
import * as ObjectUtils from '~/utils/object';
import * as UrlUtils from '~/utils/url';
import * as ValidatorUtils from '~/utils/validator';

/**
 * Get the shop currency set on Shopify store
 *
 * @returns {string} shop currency
 */
const getShopCurrency = () => window.ALLBIRDS.settings.currency || 'USD';

/**
 * Get the shop currency symbol based on the Shopify store currency
 *
 * @returns {string} shop currency symbol
 */
const getShopCurrencySymbol = () => {
  const CURRENCIES = {
    GBP: {
      symbol: '£',
    },
    USD: {
      symbol: '$',
    },
  };

  return CURRENCIES[getShopCurrency()]?.symbol || '$';
};

/**
 * This is a copy from the customer logic in
 *   - Site 1.0: https://github.com/allbirds/allbirds-shopify/blob/master/src/utils/Customer/index.js
 *   - Custom Pixels: https://github.com/allbirds/checkout-ext-custom-pixels/blob/main/src/service/customer.js
 *
 * @param {Object} existingUserTraits
 * @param {Object} proposedUserTraits
 * @param {string} email
 * @returns {boolean}
 *
 */
const shouldUpdateUser = (existingUserTraits, proposedUserTraits, email) => {
  // Update user if either
  // 1. email was captured and not already identified
  // 2. proposed traits are NOT subset of existing

  if (!email) {
    return !ObjectUtils.isMatch(existingUserTraits, proposedUserTraits);
  }
  // these emails are encrypted, we want to pull them out
  // non-deterministic due to salting means we can't compare them
  const { email: ignored, ...existingUserTraitsWithoutEncyrptedEmail } = existingUserTraits;
  const { email: Ignored, ...proposedUserTraitsWithoutEncyrptedEmail } = proposedUserTraits;

  // checking for subset here. Not all attributes present in existing may be in proposed
  // accepts_marketing for example are only present in marketing sign up actions
  // this will not overwrite if default attributes are still the same
  const shouldUpdateUser = !ObjectUtils.isMatch(
    existingUserTraitsWithoutEncyrptedEmail,
    proposedUserTraitsWithoutEncyrptedEmail,
  );

  return shouldUpdateUser;
};

/**
 * Check if a user is a returning user based on anonymous ID and last action
 *
 * @param {string} anonymousId - The anonymous ID to check
 * @param {string} currentUserState - The current user state
 * @returns {boolean} True if user is returning, false otherwise
 */
const isReturningCustomer = (anonymousId, currentUserState) => {
  // If user is not anonymous (IDENTIFIED or LOGGED_IN), they are considered returning
  if (currentUserState && currentUserState !== CustomerUtils.getAnonymousUserState()) {
    return true;
  }

  // For anonymous users, check if they have a previous action timestamp
  const lastActionTimestamp = CustomerUtils.getLastActionTimestamp(anonymousId);
  if (!lastActionTimestamp) {
    return false; // First visit
  }

  // Check if last action was more than a day ago
  const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
  return lastActionTimestamp < oneDayAgo;
};

/**
 * Pull default user traits from cookies and window object
 *
 * This is a copy from the customer logic in
 *   - Site 1.0: https://github.com/allbirds/allbirds-shopify/blob/master/src/utils/Customer/index.js
 *   - Custom Pixels: https://github.com/allbirds/checkout-ext-custom-pixels/blob/main/src/service/customer.js
 */
const getDefaultUserTraits = () => {
  const currentUserState = CustomerUtils.userState();
  const anonymousId = CustomerUtils.anonymousId();

  // Enhanced purchase detection: use server-side data when available, fallback to client-side inference
  // Server-side: customer.orders.size > 0 (most accurate)
  // Client-side: LOGGED_IN state (inference)
  const customerOrderCount = window?.ALLBIRDS?.settings?.customerOrderCount;

  // Check if user is returning based on anonymous ID and last action timestamp
  const isReturningCustomerResult = isReturningCustomer(anonymousId, currentUserState);

  // Update the last action timestamp for the current anonymous ID
  if (anonymousId) {
    CustomerUtils.setLastActionTimestamp(anonymousId, Date.now());
  }
  const defaultUserTraits = {
    allbirds_id: CustomerUtils.allbirdsId() || null,
    anonymousId,
    auth_state: currentUserState || null,
    dynamic_yield_server_id: CustomerUtils.dynamicYieldServerID() || null, // Pull from cookie
    dynamic_yield_session_id: CustomerUtils.dynamicYieldClientID() || null, // Pull from cookie
    is_loop_returns_onstore_active:
      LoopReturnsOnstoreUtils.isUtmRedirectSet() || LoopReturnsOnstoreUtils.isActive(),
    is_returning_customer: isReturningCustomerResult,
    screen_height: window?.screen?.height,
    screen_width: window?.screen?.width,
    session_id: DataDogLogs.getSessionId(),
    session_replay_session_id: CustomerUtils.sessionReplaySessionId() || null,
    shopify_domain: UrlUtils.getCurrentDomain(),
    shopify_session_id: CustomerUtils.shopifySessionId() || null,
    shopify_session_s_id: CustomerUtils.shopifySessionSId() || null,
    total_number_of_purchases: customerOrderCount,
    user_id: CustomerUtils.userId() || null,
  };

  return defaultUserTraits;
};

/**
 * Combine user additional traits with default traits
 *
 * This is a copy from the customer logic in
 *   - Site 1.0: https://github.com/allbirds/allbirds-shopify/blob/master/src/utils/Customer/index.js
 *   - Custom Pixels: https://github.com/allbirds/checkout-ext-custom-pixels/blob/main/src/service/customer.js
 *
 * @returns {Object} userTraits
 */
const getUserTraits = ({ additionalTraits = {}, email }) => {
  let supplementaryTraits = {};

  if (email) {
    const hashedEmail = HashUtils.hashText(email);
    const encryptedEmailOnCookie = CustomerUtils.encryptedEmail();
    const localEncryptedEmail = CypherUtils.encryptTextData(email);
    supplementaryTraits = {
      email: encryptedEmailOnCookie || localEncryptedEmail,
      hashed_email: hashedEmail,
      is_using_encrypted_email: true,
    };
  }
  const defaultUserTraits = getDefaultUserTraits();

  const userTraits = ObjectUtils.omitBy(
    {
      ...defaultUserTraits,
      ...supplementaryTraits,
      ...additionalTraits,
    },
    ValidatorUtils.isNil,
  );

  return userTraits;
};

/* Get user marketing email traits
 *
 * @param {Object} with parameters
 * @property {string} subscriptionSource - source or placement of the subscription
 * @property {boolen} subscriptionStatus - status of the subscription
 *
 * @returns {Object} with marketing user traits
 */
const getUserMarketingEmailTraits = ({ subscriptionSource = null, subscriptionStatus = false }) => {
  return {
    marketing_email_subscription: subscriptionStatus,
    marketing_email_subscription_source: subscriptionSource,
  };
};

/**
 * Normalize size by mimic the logic on the Shopify filter {{- variant.title | handleize -}}
 *  ie: "L (UK W8 / M8-11)" -> "l-uk-w8-m8-11"
 *
 * - Replace dollar sign with empty string
 * - Replace dots with dash
 * - Replace spaces with dash
 * - Replace pound sign with empty string
 * - Replace parentheses with empty string
 * - Replace forward slash with empty string
 * - Replace double dash with single dash
 * - Convert to lowercase
 *
 * @param {string} size
 * @returns {string} normalized size
 */
const getNormalizedSize = (size) => {
  if (!size || typeof size !== 'string') {
    return size;
  }

  return size
    ?.trim()
    ?.replaceAll('.', '-')
    ?.replaceAll('$', '')
    ?.replaceAll(' ', '-')
    ?.replaceAll('£', '')
    ?.replaceAll('(', '')
    ?.replaceAll(')', '')
    ?.replaceAll('/', '')
    ?.replaceAll('--', '-')
    ?.toLowerCase();
};

/* Pull marketing query params from the current URL
 *
 * @returns {Object} marketingQueryParams
 */
const marketingQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    fbclid: params.get('fbclid'),
    gclid: params.get('gclid'),
    msclkid: params.get('msclkid'),
  };
};

/**
 * Wrapper to 'rudderanalytics.getUserTraits()' from RudderStack SDK with error handling
 * Consider User Traits are available only after the SDK is ready 'window.rudderanalytics.ready()'
 *
 * @returns {Object} with current user traits
 */
const getCurrentUserTraits = () => {
  let result = {};

  try {
    result = window.rudderanalytics.getUserTraits();
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getCurrentUserTraits error',
      {},
      error,
    );
  }

  return result;
};

/**
 * Append event and properties to a given URL
 * - It will update given URL with the event and properties
 * - Each query param will be prefixed with 'a_ajs_prop_'
 * - Event name is passed as 'ajs_event'
 *
 * @param {URL} urlObjRef - URL to append the user traits
 * @param {string} eventName - name of the event
 * @param {Object} eventProperties - with key-value pairs of event properties
 */
const appendEventAndPropsToQueryStringUrl = ({ eventName, eventProperties, urlObjRef }) => {
  try {
    // Append event name query param
    urlObjRef.searchParams.append(QUERYSTRING_API.EVENT_NAME_PARAM, eventName);

    // Clean up event properties with empty values
    const cleanedEventProperties = ObjectUtils.omitBy(eventProperties, ValidatorUtils.isNil);

    // Appemd query params from event properties
    Object.entries(cleanedEventProperties).forEach(([key, value]) => {
      // If value is an object, serialize it
      if (typeof value === 'object') {
        value = `${QUERYSTRING_API.PROP_TYPE_OBJECT_PREFIX}${JSON.stringify(value)}`;
      }

      urlObjRef.searchParams.append(`${QUERYSTRING_API.EVENT_PROP_PREFIX_PARAM}${key}`, value);
    });

    // Check if the URL with params exceeds the browser max length allowed and log an error to be monitored
    const urlString = urlObjRef.toString();
    if (urlString.length > QUERYSTRING_API.BROWSER_MAX_URL_LENGTH) {
      DataDogLogs.error(
        '[Allbirds pixel] - RudderStack Utils - appendEventAndPropsToQueryStringUrl error - URL exceeds max length',
        { urlString },
        new Error('URL exceeds max length'),
      );
    }
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - appendEventAndPropsToQueryStringUrl error',
      {
        eventName,
        eventProperties,
        urlObjRef,
      },
      error,
    );
  }
};

/**
 * Capitalize the first letter of a string
 *
 * @param {string} str
 * @returns {string} capitalized string or string provided
 */
const capitalizeFirstLetter = (str) => {
  if (!str) {
    return str;
  }

  return String(str).charAt(0).toUpperCase() + String(str).slice(1);
};

/**
 * Format gender for RudderStack
 * - Remove single quote from gender
 * - Add 's' to the end of gender if it doesn't already have it
 * - Capitalize the first letter of the gender
 *
 * @param {string} gender
 * @returns {string} formatted gender
 */
const formatGender = (gender) => {
  if (!gender) {
    return gender;
  }

  let formattedGender = gender.replace("'", '');
  if (formattedGender.slice(-1) !== 's') {
    formattedGender += 's';
  }

  return capitalizeFirstLetter(formattedGender);
};

/**
 * Get the main hue from the hues array
 *
 * @param {Array} huesArray
 * @returns {string} main hue
 */
const getMainProductHue = (huesArray) => {
  if (!huesArray || !Array.isArray(huesArray) || huesArray.length === 0) {
    return 'no_hue';
  }

  return capitalizeFirstLetter(huesArray[0]);
};

/**
 * Get the main image from the images array
 *
 * @param {Array} imagesArray
 * @returns {string} main image or 'no_image'
 */
const getMainProductImage = (imagesArray) => {
  if (!imagesArray || !Array.isArray(imagesArray) || imagesArray.length === 0) {
    return 'no_image';
  }

  return imagesArray[0].src;
};

/**
 * Get the product name formatted for RudderStack
 *
 * @param {string} fullName
 * @param {string} masterName
 * @returns {string} formatted product name or 'no_name'
 */
const getProductName = (fullName, masterName) => {
  if (!fullName && !masterName) {
    return 'no_name';
  }

  let formattedProductName = fullName?.split(' - ')?.[0] || masterName || 'no_name';

  return formattedProductName;
};

/**
 * Check if the product is a sale product
 *
 * @param {string} priceTier
 * @returns {boolean}
 */
const isSaleProduct = (priceTier) => {
  if (!priceTier) return null;

  return !(priceTier === PRICE_TIER.MSRP);
};

/**
 * Check if the collection is a sale collection
 *
 * @param {Array} collectionPriceTiers
 *
 * @returns {boolean}
 */
const isSaleCollection = (collectionPriceTiers) => {
  if (!collectionPriceTiers) return null;

  return !collectionPriceTiers.some((priceTier) => priceTier === PRICE_TIER.MSRP);
};

/**
 * Format price to float
 *
 * @param {string|number} price
 * @returns {number|string} formatted price or NaN
 */
const getPriceFormattedToFloat = (price) => {
  if (!price) {
    return null;
  }

  return Number((price / 100).toFixed(2));
};

/**
 * Format product for RudderStack
 * The product object could contains variant information
 *
 * @param {Object} object with several properties
 * @property {Object} product - Allbirds product representation object, check utils.js-product-object.liquid
 * @property {string} sizeSelected - Size selected by the user
 * @property {number} position - Position of the product in the collection
 *
 * @returns {Object} formatted product for RudderStack
 */
const getProductFormated = ({ position = null, product, sizeSelected = null }) => {
  if (!product) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getProductFormated - product is required',
      {
        position,
        product,
        sizeSelected,
      },
      new Error('Product is required'),
    );

    return {};
  }

  let formatedProduct = {};

  try {
    formatedProduct = {
      category: capitalizeFirstLetter(product?.type),
      color_name: capitalizeFirstLetter(decodeURIComponent(product?.colorName)),
      edition: capitalizeFirstLetter(product?.edition),
      gender: formatGender(product?.gender),
      handle: product?.handle,
      hue: getMainProductHue(product?.hues),
      image_url: getMainProductImage(product?.images),
      material: capitalizeFirstLetter(product?.material),
      name: getProductName(
        decodeURIComponent(product?.fullName),
        decodeURIComponent(product?.masterName),
      ),
      on_sale: isSaleProduct(product?.priceTier),
      price: getPriceFormattedToFloat(product?.price),
      price_tier: product?.priceTier,
      product_id: product?.contentful_id || product?.id, // Fallback to Shopify product id
      silhouette: capitalizeFirstLetter(product?.silhouette),
      url: product?.url,
    };

    // If size is provided, add the variant information to the product
    if (sizeSelected) {
      const sizeNormalized = getNormalizedSize(sizeSelected);
      const variantSelected = product?.sizes?.[sizeNormalized];

      if (variantSelected) {
        formatedProduct = {
          ...formatedProduct,
          sku: variantSelected?.sku,
          variant_id: variantSelected?.id,
          variant_title: variantSelected?.title,
          variant_url: variantSelected?.url,
        };
      } else {
        DataDogLogs.error(
          '[Allbirds pixel] - RudderStack Utils - getProductFormated - variant not found',
          { product, sizeNormalized, sizeSelected },
        );
      }
    }

    // If position is provided, add the position information to the product
    if (Number.isInteger(position)) {
      formatedProduct = {
        ...formatedProduct,
        position,
      };
    }
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getProductFormated - general error',
      { product, sizeSelected },
      error,
    );
  }

  return formatedProduct;
};

/**
 * Format product image payload for RudderStack
 *
 * @param {Object} object with several properties
 * @property {number} imagePosition - The position/index of the image (0-based)
 * @property {Object} shopifyProduct - Allbirds product representation object, check 'utils.js-product-object.liquid'
 *
 * @returns {Object} formatted product image for RudderStack
 */
const getProductImageFormatted = ({ imagePosition, shopifyProduct }) => {
  if (ValidatorUtils.isNil(shopifyProduct) || ValidatorUtils.isNil(imagePosition)) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getProductImageFormatted - Missing required fields',
      { imagePosition, shopifyProduct },
      new Error('Missing required fields'),
    );
  }

  return {
    pdp_image_position: imagePosition,
    products: [
      {
        category: capitalizeFirstLetter(shopifyProduct?.type) || '',
        gender: formatGender(shopifyProduct?.gender) || '',
        handle: shopifyProduct?.handle || '',
        name:
          getProductName(
            decodeURIComponent(shopifyProduct?.fullName),
            decodeURIComponent(shopifyProduct?.masterName),
          ) || '',
        product_id: shopifyProduct?.id || shopifyProduct?.contentful_id || '',
      },
    ],
  };
};
/**
 * Get the collection type
 * On site 1.0 there were 2 types of collections "Custom Collection" and "Parent Collection"
 * On site 2.0 this concept was removed and replaced with different types of templates
 *
 * @param {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 * @returns {string} collection type
 */
const getCollectionType = (collection) => {
  let result = 'collection_type_not_set';

  if (!collection) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getCollectionType - error - collection is required',
      { collection },
      new Error('Collection is required'),
    );

    return result;
  }

  // If collection type is provided, use it
  if (collection.collection_type) {
    result = collection.collection_type;
  }

  return result;
};

/**
 * Get the collection parent
 * Same logic as the 'breadcrumbs.liquid' snippet
 *
 * @param {Object} collection
 * @returns {string} collection parent
 */
const getCollectionParentTitle = (collection) => {
  if (!collection) {
    return COLLECTION_PARENT_TYPES.NO_PARENT;
  }

  try {
    // First:
    // - Attempt to get the parent collection from the collection metafield
    if (collection.parent_collection_breadcrumb?.title) {
      return collection.parent_collection_breadcrumb.title;
    }

    // Second:
    // - If no collection metafield is set, generate the gender parent collection
    if (collection.handle.startsWith('mens-')) {
      return COLLECTION_PARENT_TYPES.MENS;
    }
    if (collection.handle.startsWith('womens-')) {
      return COLLECTION_PARENT_TYPES.WOMENS;
    }

    // Default:
    // - Return 'Home' if no parent collection is found
    return COLLECTION_PARENT_TYPES.HOME;
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getCollectionParentTitle error pulling parent collection',
      { collection },
      error,
    );

    return COLLECTION_PARENT_TYPES.NO_PARENT;
  }
};

/**
 * Format collection for RudderStack
 *
 * @param {Object} param - object with parameters
 * @property {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 * @property {boolean} includeProducts - Whether to include products in the collection transformation
 *
 * @returns {Object} formatted collection for RudderStack
 */
const getCollectionFormatted = ({ collection, includeProducts = true }) => {
  if (!collection) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getCollectionFormatted error',
      {},
      new Error('Collection is required'),
    );
    return {};
  }

  let collectionFormated = {};

  try {
    collectionFormated = {
      collection_handle: collection.handle,
      collection_name: decodeURIComponent(collection.title),
      collection_style_count: collection?.products?.length || 0,
      collection_type: getCollectionType(collection),
      collection_url: `https://${UrlUtils.getCurrentDomain()}${collection.url}`,
      list_id: collection.contentful_id,
    };

    // Parent Collection has additional properties
    if (collection.collection_type === COLLECTION_TYPES.PARENT_COLLECTION) {
      collectionFormated = {
        ...collectionFormated,
        collection_parent: decodeURIComponent(getCollectionParentTitle(collection)),
        is_markdown_collection: isSaleCollection(collection.price_tiers),
      };
    }

    // Check if products should be included  in the collection transformation
    if (includeProducts) {
      collectionFormated = {
        ...collectionFormated,
        products:
          collection?.products?.map((productItem, index) =>
            getProductFormated({ position: index, product: productItem }),
          ) || [],
      };
    }
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getCollectionFormatted error',
      { collection },
      error,
    );
  }

  return collectionFormated;
};

/**
 * Format collection filters for RudderStack
 *
 * @param {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 * @returns {Object} formatted collection filters for RudderStack
 */
const getCollectionFiltersFormated = (collection) => {
  if (!collection || !collection.filters_applied) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getCollectionFiltersFormated error',
      {
        collection,
        filtersApplied: collection?.filters_applied,
      },
      new Error('Collection is required'),
    );

    return {};
  }

  return collection.filters_applied.map((filter) => ({
    type: filter?.type?.toLowerCase(),
    value: filter?.value?.toLowerCase(),
  }));
};

/**
 * Get / infer the gender from the product name
 *
 * @param {string} productName
 * @returns {string} gender
 */
const getGenderFromProductName = (productName) => {
  if (!productName) {
    return 'gender_not_found';
  }

  const firstWord = productName.split(' ')[0];

  // If first word includes 'men' meaning could be 'Men's' or 'Women's', return the gender formatted
  if (firstWord.toLowerCase().includes('men')) {
    return formatGender(firstWord);
  }

  // If first word is 'small', return 'Toddler'
  if (firstWord.toLowerCase().includes('small')) {
    return 'Toddler';
  }

  // Defaults to Unisex if it's not a Mens/Womens/Toddler product
  return 'Unisex';
};

/**
 * Format cart items / products for RudderStack
 * The cart items / products format is based on Shopify AJAX Cart API response https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
 *
 * @property {Array} ajaxCartItems - List of cart items from AJAX Cart API https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
 *
 * @returns {Array} formatted cart items / products
 */
const getAjaxApiCartItemsFormatted = (ajaxCartItems) => {
  if (!Array.isArray(ajaxCartItems)) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - getAjaxApiCartItemsFormatted error',
      {
        ajaxCartItems,
      },
      new Error('Cart items are required'),
    );

    return [];
  }

  // TODO: Review missing properties, task: https://app.asana.com/1/209680782572886/project/1208717489402917/task/1210454246576949?focus=true
  return ajaxCartItems.map((ajaxCartItem, index) => {
    return {
      category: ajaxCartItem?.product?.product_type,
      color_name: ajaxCartItem?.properties?._color_name,
      // edition: 'Limited', // TODO: Check if needed or get rid, not present on AJAX response
      gender: getGenderFromProductName(ajaxCartItem?.properties?._master_name),
      handle: ajaxCartItem?.handle,
      // hue: 'Orange', // TODO: Check if needed or get rid, not present on AJAX response
      image_url: ajaxCartItem?.image,
      // material: 'Tree', // TODO: Check if needed or get rid, not present on AJAX response
      name: ajaxCartItem?.properties?._master_name,
      on_sale: ajaxCartItem?.price != ajaxCartItem?.original_price,
      position: index,
      price: ajaxCartItem?.price,
      // price_tier: 'msrp', // TODO: Check if needed or get rid, not present on AJAX response
      product_id: ajaxCartItem?.product_id,
      quantity: ajaxCartItem?.quantity,
      // silhouette: 'Dasher', // TODO: Check if needed or get rid, not present on AJAX response
      sku: ajaxCartItem?.sku,
      url: `/products/${ajaxCartItem?.handle}`,
      variant_id: ajaxCartItem?.variant_id,
      variant_title: ajaxCartItem?.variant_title,
      variant_url: `/products/${ajaxCartItem?.handle}?size=${getNormalizedSize(ajaxCartItem?.variant_title)}`,
    };
  });
};

/**
 * Clean all query string API parameters from the URL
 * Query string API parameters are prefixed with 'a_ajs_', check 'constants/rudderstack.js'
 *
 * @param {string} url - URL to clean query string API parameters
 * @returns {string} URL with query string API parameters cleaned
 */
const cleanAllQueryStringAPI = (urlToClean = window.location.href) => {
  try {
    const urlObj = new URL(urlToClean);
    const paramsResult = new URLSearchParams();

    // If there are no query string, return the original URL
    if (!urlObj.search) {
      return urlToClean;
    }

    for (const [urlParamKey, urlParamValue] of urlObj.searchParams) {
      // If the parameter key does not start with the query string API prefix, add it to the result
      if (
        !(
          urlParamKey?.startsWith(QUERYSTRING_API.EVENT_PROP_PREFIX_PARAM) ||
          urlParamKey?.startsWith(QUERYSTRING_API.EVENT_NAME_PARAM) ||
          urlParamKey?.startsWith(QUERYSTRING_API.PROP_TYPE_OBJECT_PREFIX)
        )
      ) {
        paramsResult.append(urlParamKey, urlParamValue);
      }
    }

    // Build the URL with the cleaned query string API parameters
    return `${urlObj.origin}${urlObj.pathname}${paramsResult.toString() !== '' ? `?${paramsResult.toString()}` : ''}`;
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Utils - cleanAllQueryStringAPI error',
      { urlToClean },
      error,
    );

    return urlToClean;
  }
};

export {
  appendEventAndPropsToQueryStringUrl,
  cleanAllQueryStringAPI,
  getAjaxApiCartItemsFormatted,
  getCollectionFiltersFormated,
  getCollectionFormatted,
  getCurrentUserTraits,
  getNormalizedSize,
  getProductFormated,
  getProductImageFormatted,
  getShopCurrency,
  getShopCurrencySymbol,
  getUserMarketingEmailTraits,
  getUserTraits,
  marketingQueryParams,
  shouldUpdateUser,
};
